import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NUM, fmtDate } from "@/lib/format";
import { Inbox, Search, Tag, User, CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";

const ALL = "__all";
const norm = (s) => String(s || "").toLocaleLowerCase("tr");

// Açık (bekliyor) talepler listesi — istenenler işaretlenir ve sipariş çıktısına aktarılır
export default function OpenOrdersPicker({ open, onOpenChange, orders, initialBrand, importedIds, onImport }) {
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState(ALL);
  const [sel, setSel] = useState(new Set());
  useEffect(() => { if (open) { setQ(""); setSel(new Set()); setBrand(initialBrand || ALL); } }, [open, initialBrand]);
  const brands = useMemo(() => [...new Set(orders.map((o) => (o.brand || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [orders]);
  const list = useMemo(() => {
    const nq = norm(q);
    return orders.filter((o) => (brand === ALL || (o.brand || "") === brand) && (!nq || norm(`${o.product_name} ${o.brand} ${o.code} ${o.user_name} ${o.note}`).includes(nq)));
  }, [orders, brand, q]);
  const selectable = list.filter((o) => !importedIds.has(o.id));
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSel = selectable.length > 0 && selectable.every((o) => sel.has(o.id));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(selectable.map((o) => o.id)));
  const doImport = () => { const chosen = orders.filter((o) => sel.has(o.id)); if (!chosen.length) return; onImport(chosen); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="open-orders-picker">
        <DialogHeader className="px-5 pt-4 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 font-head"><Inbox className="h-5 w-5 text-primary" />Açık Talepler</DialogTitle>
          <DialogDescription>İstediğiniz açık talepleri işaretleyin; seçilenler sipariş çıktısına kalem olarak eklenir.</DialogDescription>
        </DialogHeader>
        <div className="px-5 py-2.5 border-b bg-slate-50/70 flex items-center gap-2">
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="h-9 w-48 bg-white" data-testid="oop-brand"><Tag className="h-3.5 w-3.5 mr-1.5 text-primary shrink-0" /><SelectValue placeholder="Marka" /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value={ALL}>Tüm Markalar</SelectItem>{brands.map((b) => <SelectItem key={b} value={b} data-testid={`oop-brand-${b}`}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün / kod / talep eden ara…" className="pl-8 h-9 bg-white" data-testid="oop-search" />
          </div>
          <label className="inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer select-none whitespace-nowrap">
            <Checkbox checked={allSel} onCheckedChange={toggleAll} disabled={!selectable.length} data-testid="oop-select-all" />Tümünü seç ({selectable.length})
          </label>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="oop-list">
          {list.length === 0 ? (
            <div className="py-14 text-center text-sm text-muted-foreground">Eşleşen açık talep yok.</div>
          ) : list.map((o) => {
            const done = importedIds.has(o.id);
            return (
              <label key={o.id} className={cn("flex items-center gap-3 px-5 py-2.5 border-b last:border-0 cursor-pointer hover:bg-muted/50 transition-colors", done && "bg-emerald-50/60 cursor-default")} data-testid={`oop-row-${o.id}`}>
                <Checkbox checked={sel.has(o.id)} onCheckedChange={() => toggle(o.id)} disabled={done} data-testid={`oop-check-${o.id}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold leading-snug">{o.product_name}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                    {o.brand && <span className="rounded-full bg-secondary/10 text-secondary px-1.5 font-semibold">{o.brand}</span>}
                    {o.code && <span className="font-mono">{o.code}</span>}
                    <span className="inline-flex items-center gap-0.5"><User className="h-3 w-3" />{o.user_name || "—"}</span>
                    <span>{fmtDate(o.created_at)}</span>
                    {!o.in_stock && <span className="text-amber-700 font-semibold">stokta yok</span>}
                    {o.note && <span className="italic truncate max-w-[260px]">“{o.note}”</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-black tabular-nums text-[#0B192C]" data-testid={`oop-qty-${o.id}`}>{NUM(o.requested_qty)}</div>
                  <div className="text-[10px] text-muted-foreground uppercase">{o.unit || "adet"}</div>
                </div>
                {done && <span className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-0.5 shrink-0"><CheckCheck className="h-3 w-3" />Formda</span>}
              </label>
            );
          })}
        </div>
        <DialogFooter className="px-5 py-3 border-t bg-slate-50/70 sm:justify-between">
          <span className="text-xs text-muted-foreground self-center">{sel.size} talep seçildi</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="oop-cancel">Vazgeç</Button>
            <Button onClick={doImport} disabled={!sel.size} data-testid="oop-import"><Inbox className="h-4 w-4 mr-1.5" />Seçilenleri Aktar ({sel.size})</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
